import { inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap, combineLatest, map, withLatestFrom, filter, skip, debounceTime, of, concatMap } from 'rxjs';
import { DefinitionsStyle, ApproverDefinitionItem } from '../models/definitionsStyle.model';
import { MailForm } from '../models/mailForm';
import { Question } from '../models/question.model';
import { DistributionDetailsRequest, Questionnaire } from '../models/questionnaire.model';
import { QuestionnaireCategory } from '../models/questionnaireCategory.model';
import { Reminder } from '../models/reminder';
import { Step } from '../models/step.model';
import { QuestionnaireApiService } from '../services/questionnaire-api.service';
import { SendSuccessComponent } from '../features/create-questionnaire-page/component/send-success/send-success.component';
import { MatDialog } from '@angular/material/dialog';
import { ImageBase64 } from '../models/imageBase64.models';
import { toSignal } from '@angular/core/rxjs-interop';
import { Recipients } from '../models/recipients.model';
import { EStatus } from '../models/EnumStatus';
import { GlobalStore } from './global-store';
import { EQuestionnaireStatus } from '../models/questionnaireGrid.model';
import { QuestionnairesGridStore } from '../features/questionnaires/store/questionnairesGrid-store';
import { Router } from '@angular/router';
import { TostPositionEnum, TostTypeEnum } from '../shared/models/tostes.model';
import { TostesStore } from './tostes-store';
import { GlobalService } from '../shared/services/global-service';
import { EImageStatus } from '../models/EnumImageStatus';
import { DragStore } from './drag-store';
import { mapGuids } from '../models/changeGuidsRespons';

export interface QuestionnaireState {
  readonly questionnaire: Questionnaire;
  readonly questionnaireId: number;
  readonly currentStepIndex: number;
  readonly loading: boolean;
  readonly loadingSave: boolean;
  readonly loadingDistributed: boolean;
  readonly isChanged: boolean;
  readonly isFilling: boolean;
  readonly isValid: boolean;
  readonly recentlySave?: Date;
  readonly imagesToQuestionnaire?: ImageBase64[];
  readonly loadDeleteImg: boolean;
  readonly isAfterInitDefinitions: boolean;
  readonly changeNotSavedInCurrentStep: boolean;
  readonly unnecessaryChangesInCurrentStep: number;
  readonly canUserEditQuestionnaire: boolean;
  readonly addingStepNow?: boolean;
  error?: string;
}
export class QuestionnaireStore extends ComponentStore<QuestionnaireState> {
  #questionnaireApiService = inject(QuestionnaireApiService);
  #router = inject(Router)
  #tostesStore = inject(TostesStore);
  #globalStore = inject(GlobalStore);
  #globalService = inject(GlobalService);
  #dragStore = inject(DragStore);
  #questionnarieGridStore = inject(QuestionnairesGridStore);
  #dialog = inject(MatDialog);
  loadingAnswer$ = inject(GlobalStore).loadingAnswer$;
  constructor() {
    //values to configuration
    const baseColorMain = "#1390CF";
    const baseColorSeconary = "#D1EFFF";
    const baseColorBack = "#F0F3FA";

    super({
      loading: false,
      loadingSave: false,
      loadingDistributed: false,
      isChanged: false,
      isValid: true,
      isFilling: false,
      questionnaireId: 0,
      questionnaire: {
        id: 0, steps: [{ id: 0, questions: [], status: EStatus.Created, title: "Page 1" }]
        , definitions: { name: '', mainColor: baseColorMain, secondaryColor: baseColorSeconary, backColor: baseColorBack }
      },
      isAfterInitDefinitions: false,
      currentStepIndex: -1,
      loadDeleteImg: false,
      changeNotSavedInCurrentStep: false,
      unnecessaryChangesInCurrentStep: 0,
      canUserEditQuestionnaire: true
    });

    combineLatest([this.definitions$, this.category$]).pipe(
      withLatestFrom(this.questionnaire$, this.isAfterInitDefinitions$),
      filter(([[definitions, category], questionnaire, isAfterInitDefinitions]) => questionnaire.status == EQuestionnaireStatus.Edit && isAfterInitDefinitions),
      debounceTime(1000),
    ).subscribe(
      ([[definitions, category], questionnaire, isAfterInitDefinitions]) => {
        this.#dragStore.updateDoScreenImage(true);
        this.saveDefinitions({ definitions: definitions, category: category })
      }
    )

    this.addingStepNow$.subscribe(x =>
      this.#dragStore.updateAddingStepNow(x)
    )
  }

  readonly questionnaire$ = this.select(({ questionnaire }) => questionnaire);
  readonly questionnaireId$ = this.select(({ questionnaireId }) => questionnaireId);
  readonly apiExposureEnabled$ = this.select(({ questionnaire }) => questionnaire.definitions?.apiExposureEnabled ?? false);
  readonly isUnauthorized$ = this.select(({ questionnaire }) => questionnaire.definitions?.isUnauthorized ?? false);
  readonly filterFieldId$ = this.select(({ questionnaire }) => questionnaire.steps.flatMap(s => s.questions).find(q => q.isFilterField)?.id ?? null);
  readonly usedAutoFillFieldIds$ = this.select(({ questionnaire }) => questionnaire.steps.flatMap(s => s.questions)
    .filter(q => q.autoFillFieldId != null)
    .map(q => ({ questionId: q.id, autoFillFieldId: q.autoFillFieldId! })));
  readonly mailForm$ = this.select(({ questionnaire }) => { return { subject: questionnaire.mailSubject, body: questionnaire.mailBody, submissionDate: questionnaire.submissionDateNTZ } });
  readonly reminder$ = this.select(({ questionnaire }) => questionnaire.reminder);
  readonly loading$ = this.select(({ loading }) => loading);
  readonly notLoading$ = combineLatest([this.loading$, this.loadingAnswer$]).pipe(map(([loading, answersLoading]) => !loading && !answersLoading))
  readonly notLoadingSave$ = this.select(({ loadingSave }) => !loadingSave);
  readonly loadingDistributed$ = this.select(({ loadingDistributed }) => loadingDistributed);
  readonly isValid$ = this.select(({ isValid }) => isValid);
  readonly changeNotSavedInCurrentStep$ = this.select(({ changeNotSavedInCurrentStep }) => changeNotSavedInCurrentStep);
  readonly unnecessaryChangesInCurrentStep$ = this.select(({ unnecessaryChangesInCurrentStep }) => unnecessaryChangesInCurrentStep);
  readonly steps$ = this.select(({ questionnaire }) => questionnaire.steps);
  readonly stepNames$ = this.select(({ questionnaire }) => questionnaire.steps.map(step => { return { title: step.title, id: step.id } }));
  readonly stepsSig$ = toSignal(this.select(({ questionnaire }) => questionnaire.steps));
  readonly stepsLength$ = this.select(({ questionnaire }) => questionnaire.steps.length);
  readonly recipients$ = this.select(({ questionnaire }) => questionnaire.recipients);
  readonly name$ = this.select(({ questionnaire }) => questionnaire.definitions?.name);
  readonly binding$ = this.select(({ questionnaire }) => { return { name: questionnaire.definitions?.coverName, base64: questionnaire.definitions?.coverImage } });
  readonly status$ = this.select(({ questionnaire }) => questionnaire.status);
  readonly definitions$ = this.select(({ questionnaire }) => questionnaire.definitions);
  readonly mainColor$ = this.select(({ questionnaire }) => questionnaire.definitions?.mainColor);
  readonly backColor$ = this.select(({ questionnaire }) => questionnaire.definitions?.backColor);
  readonly secondaryColor$ = this.select(({ questionnaire }) => questionnaire.definitions?.secondaryColor);
  readonly logo$ = this.select(({ questionnaire }) => questionnaire.definitions?.logo);
  readonly category$ = this.select(({ questionnaire }) => questionnaire.category);
  readonly currentStepIndex$ = this.select(({ currentStepIndex }) => currentStepIndex);
  readonly isUser$ = this.select(({ isFilling }) => isFilling);
  readonly isUserSig$ = toSignal(this.isUser$);
  readonly currentStep$ = combineLatest([this.select(x => x.questionnaire.steps), this.currentStepIndex$]).pipe(
    map(([steps, index]) => steps[index]));

  readonly isSendAll$ = this.select(({ questionnaire }) => questionnaire.isSendAll);
  readonly currentStepQuestions$ = combineLatest([this.select(x => x.questionnaire.steps), this.select(x => x.currentStepIndex)]).pipe(
    map(([steps, index]) => steps[index]?.questions));
  readonly isCurrentStepQuestions$ = this.currentStepQuestions$.pipe(map(q => q?.length ?? 0 > 0))
  readonly recentlySave = this.select(({ recentlySave }) => recentlySave);
  readonly loadDeleteImg$ = this.select(({ loadDeleteImg }) => loadDeleteImg);
  readonly error$ = this.select(({ error }) => error);
  readonly canUserEditQuestionnaire$ = this.select(({ canUserEditQuestionnaire }) => canUserEditQuestionnaire);
  readonly addingStepNow$ = this.select(({ addingStepNow }) => addingStepNow);
  readonly isAfterInitDefinitions$ = this.select(({ isAfterInitDefinitions }) => isAfterInitDefinitions);
  readonly isApprovalRequired$ = this.select(({ questionnaire }) => questionnaire.definitions?.isApprovalRequired);
  readonly approversType$ = this.select(({ questionnaire }) => questionnaire.definitions?.approvers);
  readonly isNotificationEnabled$ = this.select(({ questionnaire }) => questionnaire.definitions?.isNotificationEnabled);
  readonly notificationEmail$ = this.select(({ questionnaire }) => questionnaire.definitions?.notificationEmail);

  readonly updateAfterInitDefinitions = this.updater((state) => (
    {
      ...state,
      isAfterInitDefinitions: true
    }
  ))
  readonly updateQuestionnaire = this.updater((state, questionnaire: Questionnaire) => ({
    ...state,
    error: undefined,
    questionnaire
  }));
  readonly updatSteps2 = this.updater((state, updatSteps: Step[]) => ({
    ...state,
    questionnaire: { ...state.questionnaire, steps: updatSteps }
  }));
  readonly updatValid = this.updater((state, isValid: boolean) => ({
    ...state,
    isValid: isValid
  }));
  readonly updateWasChangeNotSave = this.updater((state, isChanged: boolean) => ({
    ...state,
    changeNotSavedInCurrentStep: isChanged
  }));
  readonly addUnnecessaryChanges = this.updater((state, isAdding: boolean) => ({
    ...state,
    unnecessaryChangesInCurrentStep: isAdding ? (state.unnecessaryChangesInCurrentStep + 1) : (state.unnecessaryChangesInCurrentStep - 1)
  }));
  readonly resetUnnecessaryChanges = this.updater((state) => ({
    ...state,
    unnecessaryChangesInCurrentStep: 0
  }));
  readonly updateLogo = this.updater((state, logo: string) => ({
    ...state,
    isChanged: true,
    questionnaire: { ...state.questionnaire, definitions: { name: "", mainColor: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, logo } }
  }));
  readonly updateBinding = this.updater((state, binding: { name: string, base64: string } | null) => ({
    ...state,
    isChanged: true,
    questionnaire: { ...state.questionnaire, definitions: { name: "", mainColor: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, coverName: binding?.name, coverImage: binding?.base64 } }
  }));
  readonly updatDefinitionsName = this.updater((state, nameQ: string | undefined) => ({
    ...state,
    isChanged: state.questionnaire.definitions?.name == nameQ ? state.isChanged : true,
    questionnaire: {
      ...state.questionnaire,
      definitions: {
        mainColor: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, name: nameQ ?? ""
      }
    }
  }));

  readonly updatDefinitionsApiExposureEnabled = this.updater((state, apiExposureEnabledQ: boolean | undefined) => ({
    ...state,
    isChanged: state.questionnaire.definitions?.apiExposureEnabled === apiExposureEnabledQ ? state.isChanged : true,
    questionnaire:
    {
      ...state.questionnaire,
      definitions: { mainColor: "", name: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, apiExposureEnabled: apiExposureEnabledQ }
    }
  }));

  readonly updatDefinitionsIsUnauthorized = this.updater((state, isUnauthorizedQ: boolean | undefined) => ({
    ...state,
    isChanged: state.questionnaire.definitions?.isUnauthorized === isUnauthorizedQ ? state.isChanged : true,
    questionnaire:
    {
      ...state.questionnaire,
      definitions: { mainColor: "", name: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, isUnauthorized: isUnauthorizedQ }
    }
  }));
  readonly updatDefinitionsIsApprovalRequired = this.updater((state, isApprovalRequiredQ: boolean | undefined) => ({
    ...state,
    isChanged: state.questionnaire.definitions?.isApprovalRequired === isApprovalRequiredQ ? state.isChanged : true,
    questionnaire:
    {
      ...state.questionnaire,
      definitions: { mainColor: "", name: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, isApprovalRequired: isApprovalRequiredQ }
    }
  }));

  readonly updatDefinitionsIsNotificationEnabled = this.updater((state, isNotificationEnabledQ: boolean | undefined) => ({
    ...state,
    isChanged: state.questionnaire.definitions?.isNotificationEnabled === isNotificationEnabledQ ? state.isChanged : true,
    questionnaire:
    {
      ...state.questionnaire,
      definitions: { mainColor: "", name: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, isNotificationEnabled: isNotificationEnabledQ }
    }
  }));

  readonly updatDefinitionsNotificationEmail = this.updater((state, notificationEmailQ: string | undefined) => ({
    ...state,
    isChanged: state.questionnaire.definitions?.notificationEmail === notificationEmailQ ? state.isChanged : true,
    questionnaire:
    {
      ...state.questionnaire,
      definitions: { mainColor: "", name: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, notificationEmail: notificationEmailQ }
    }
  }));

readonly updatApproversType = this.updater((state, approversType: ApproverDefinitionItem[]) => {
  const current = state.questionnaire.definitions?.approvers ?? [];

  const areEqual =
    current.length === approversType.length &&
    current.every((x, i) => x.roleNumber === approversType[i].roleNumber && x.groupId === approversType[i].groupId);

  return {
    ...state,
    isChanged: areEqual ? state.isChanged : true,
    questionnaire: {
      ...state.questionnaire,
      definitions: {
        mainColor: "",
        name: "",
        secondaryColor: "",
        backColor: "",
        ...state.questionnaire.definitions,
        approvers: approversType
      }
    }
  };
});

  readonly updatDefinitionsCategory = this.updater((state, category: QuestionnaireCategory | undefined) => ({
    ...state,
    isChanged: state.questionnaire.category == category ? state.isChanged : true,
    questionnaire: { ...state.questionnaire, category: category ?? state.questionnaire.category }
  }));
  readonly updateCoverImage = this.updater((state, file: { coverImage: string, name: string }) => ({
    ...state
  }));
  readonly updateIsInFillQuestionnaire = this.updater((state, isFilling: boolean) => ({
    ...state,
    isFilling
  }));
  readonly updateStepNameLocal = this.updater((state, step: { name: string, index: number }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      steps: [
        ...state.questionnaire.steps.map((s, i) => ({
          ...s,
          title: i == step.index ? step.name : s.title,
          status: i == step.index ?
            s.status == EStatus.Saved ?
              EStatus.Changed
              : s.status
            : s.status
        }))
      ]
    },
  }));

  readonly updateStepName = this.effect<{ name: string, index: number }>(
    pipe(
      tap((x) => this.updateStepNameLocal(x)),
      withLatestFrom(this.questionnaire$),
      switchMap(([props, questionnaire]) => {
        return this.#questionnaireApiService.updateStepName(props.name, questionnaire.steps[props.index].id, questionnaire.id)
          .pipe(
            tapResponse(
              (x) => (
                this.patchState({ canUserEditQuestionnaire: true })
              ),
              (error: Error) => {
                this.patchState({ error: error?.message, canUserEditQuestionnaire: false });
                this.addCannotEditTost();
              }
            )
          )
      }
      )
    )
  );

  readonly updateCurrentStepIndex = this.updater((state, stepIndex: number) => ({
    ...state,
    currentStepIndex: stepIndex
  }));
  readonly nextStepIndex = this.updater((state) => ({
    ...state,
    currentStepIndex: state.currentStepIndex + 1
  }));
  readonly prevStepIndex = this.updater((state) => ({
    ...state,
    currentStepIndex: state.currentStepIndex - 1
  }));
  readonly saveContact = this.updater((state, users: Recipients) => ({
    ...state,
    questionnaire: { ...state.questionnaire, recipients: users }
  }));
  readonly saveMailDetails = this.updater((state, mailForm: MailForm) => ({
    ...state,
    questionnaire: { ...state.questionnaire, mailSubject: mailForm.subject, mailBody: mailForm.body, submissionDateNTZ: mailForm.submissionDateNTZ }
  }));
  readonly saveIsSendAll = this.updater((state, isSendAll: boolean) => ({
    ...state,
    questionnaire: { ...state.questionnaire, isSendAll: isSendAll }
  }));

  readonly addQuestionToCurrentStep = this.updater((state, question: Question) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      steps: [...state.questionnaire.steps.map((x, i) => {
        if (i == state.currentStepIndex)
          return { ...x, questions: [...x.questions, question], status: x.status == EStatus.Created ? EStatus.Created : EStatus.Changed };
        else
          return x;
      }
      )]
    }
  }));
  readonly MoveStepTop = this.updater((state, stepIndex: number) => {
    if (stepIndex >= 1) {
      const newSteps = [...state.questionnaire.steps];
      const temp = newSteps[stepIndex];
      newSteps[stepIndex] = {
        ...newSteps[stepIndex - 1], status: newSteps[stepIndex - 1].status == EStatus.Saved ? EStatus.Changed : newSteps[stepIndex - 1].status
      };
      newSteps[stepIndex - 1] = { ...temp, status: temp.status == EStatus.Saved ? EStatus.Changed : temp.status };
      this.SaveStepOrders(newSteps);
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          steps: newSteps,
          stepsName: [...newSteps.map(x => x.title)]
        },
        currentStepIndex: stepIndex - 1,

      };
    } else {
      return { ...state };
    }
  });
  readonly MoveStepBottom = this.updater((state, stepIndex: number) => {
    if (stepIndex < state.questionnaire.steps.length - 1 && stepIndex >= 0) {
      const newSteps = [...state.questionnaire.steps];
      const temp = newSteps[stepIndex];
      newSteps[stepIndex] = {
        ...newSteps[stepIndex + 1], status: newSteps[stepIndex + 1].status == EStatus.Saved ? EStatus.Changed : newSteps[stepIndex + 1].status
      };
      newSteps[stepIndex + 1] = { ...temp, status: temp.status == EStatus.Saved ? EStatus.Changed : temp.status };

      this.SaveStepOrders(newSteps);
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          steps: newSteps
        },
        currentStepIndex: stepIndex + 1,

      };
    } else {
      return { ...state };
    }
  });
  readonly GetDuplicateStep = (step: Step, title?: string) => {
    var mapGuidsArrOfAllStep = [] as mapGuids[]
    const duplicateQuesions = [...step.questions.map(q => {
      var changeGuidRespons = this.#globalService.changeGuidsOfQuestions(q);
      mapGuidsArrOfAllStep = [...mapGuidsArrOfAllStep, ...changeGuidRespons.mapGuids ?? []]
      var newQuestion = changeGuidRespons.question;
      if (q.parentFollowUpQuestionId) {
        var newGuid = mapGuidsArrOfAllStep.find(mg => mg.previousGuid == q.followAnswerOptionItemGuid)?.newGuid;
        if (newGuid) {
          newQuestion = { ...newQuestion, followAnswerOptionItemGuid: newGuid };
        }
      }
      return {
        ...newQuestion,
        status: EStatus.Created,
      } as Question
    })];
    //אם צריך להעביר את החלפת גוידס לסוף - אבל זה אמור להיות לפי סדר 
    // const DuplicateQuestionsWithMapGuids = duplicateQuesions.map(q => { return { ...q } });
    const duplicatedStep = { id: 0, status: EStatus.Created, title: `${title ?? step.title} copy`, questions: duplicateQuesions } as Step;
    return duplicatedStep;
  };
  readonly DuplicateStep = this.effect<{ index: number, currentStep: Step }>(
    pipe(
      tap((props) => this.patchState({ loadingSave: true })),
      withLatestFrom(this.questionnaire$, this.currentStepIndex$),
      map(([props, questionnaire, curentStepIndex]) => {
        var duplicateStep = null;
        if (props.index == curentStepIndex) {
          duplicateStep = this.GetDuplicateStep(props.currentStep, questionnaire.steps[props.index].title);
        }
        else {
          if (props.index < questionnaire.steps.length && props.index >= 0) {
            duplicateStep = this.GetDuplicateStep(questionnaire.steps[props.index])
          }
        }
        return { stepIndex: props.index, questionnaire, duplicateStep };
      }),
      switchMap((props) => {
        if (props.duplicateStep) {
          return this.#questionnaireApiService.duplicateStep(props.questionnaire.id, props.stepIndex + 1, props.duplicateStep, [...props.questionnaire.steps.map((s, i) => { return { id: s.id, order: i + 2 } }).filter((s) => s.order > props.stepIndex + 2)])
            .pipe(
              tapResponse(
                (duplicateStepResult) => {
                  const newSteps = [...props.questionnaire.steps];
                  newSteps.splice(props.stepIndex + 1, 0, duplicateStepResult);
                  this.patchState({
                    questionnaire: {
                      ...props.questionnaire,
                      steps: [...newSteps]
                    },
                    currentStepIndex: props.stepIndex + 1,

                    loadingSave: false,
                    canUserEditQuestionnaire: true
                  })
                },
                (error: Error) => {
                  this.patchState({ error: error?.message, loadingSave: false, canUserEditQuestionnaire: false })
                  this.addCannotEditTost();
                }
              )
            )
        }
        else {
          return of();
        }
      }
      )
    )
  );
  readonly DeleteStep = this.effect<number>(
    pipe(
      tap((x) => this.patchState({ loadingSave: true })),
      withLatestFrom(this.questionnaire$, this.currentStepIndex$),
      tap(([index, questionnaire, currentStepIndex]) => this.deleteStepLocal(questionnaire, currentStepIndex, index)),
      switchMap(([index, questionnaire, currentStepIndex]) => {
        return this.#questionnaireApiService.deleteStepById(questionnaire.steps[index].id, questionnaire.id)
          .pipe(
            tapResponse(
              (q) => {
                this.deleteStepLocal(questionnaire, currentStepIndex, index)
                this.patchState({ canUserEditQuestionnaire: true })
              },
              (error: Error) => {
                this.patchState({ error: error?.message, loadingSave: false, canUserEditQuestionnaire: false });
                this.addCannotEditTost();
              }
            )
          )
      }
      )
    )
  );

  readonly AddStep = this.effect<void>(
    pipe(
      tap((x) => this.patchState({ addingStepNow: true })),
      withLatestFrom(this.questionnaire$, this.stepsLength$),
      switchMap(([_, questionnaire, length]) => {
        return this.#questionnaireApiService.createEmptyStep(questionnaire.id, length + 1)
          .pipe(
            tapResponse(
              (newStep) => this.patchState({ addingStepNow: false, currentStepIndex: length, questionnaire: { ...questionnaire, steps: [...questionnaire.steps, newStep] }, canUserEditQuestionnaire: true }),
              (error: Error) => {
                this.patchState({ error: error?.message, addingStepNow: undefined, canUserEditQuestionnaire: false });
                this.addCannotEditTost();
              }
            )
          )
      }
      )
    )
  );
  readonly SaveImageFromLoad = this.updater((state, props: { questionId: number, image: string }) => {
    return {
      ...state,
      questionnaire: { ...state.questionnaire, steps: [...state.questionnaire.steps.map((s, index) => { return { ...s, questions: s.questions.map(q => q.id == props.questionId ? { ...q, imageBase64: props.image, statusImage: EImageStatus.Saved } : q) } })] }
    };
  })
  readonly SaveStepLocal = this.updater((state, step: Step) => {
    return {
      ...state,
      questionnaire: { ...state.questionnaire, steps: [...state.questionnaire.steps.map(s => s.id == step.id ? { ...step, title: s.title } : s)] }
    }
  });

  readonly SaveStep = this.effect<{ step: Step, imgScreen?: string, isToLogg: boolean }>(
    pipe(
      tap((x) => {
        console.log('SaveStep effect - step data:', x.step);
        console.log('SaveStep effect - questions:', x.step.questions);
        this.patchState({ loadingSave: true, changeNotSavedInCurrentStep: false });
      }),
      withLatestFrom(this.questionnaire$),
      concatMap(([props, questionnaire]) => {
        this.patchState({ questionnaire: { ...questionnaire, steps: [...questionnaire.steps.map(s => s.id == props.step.id ? { ...props.step, title: s.title } : s)] } });
        console.log('SaveStep - sending to API:', { ...props.step, title: '' });
        return this.#questionnaireApiService.saveStep(questionnaire.id, { ...props.step, title: '' }, props.isToLogg, props.imgScreen)
          .pipe(
            tapResponse(
              (newStep) => {
                this.patchState({
                  loadingSave: false,
                  questionnaire: { ...questionnaire, steps: [...questionnaire.steps.map(s => s.id == newStep.id ? newStep : s)] }
                  , canUserEditQuestionnaire: true
                })
              },
              (error: Error) =>
                this.patchState({ error: error?.message, loadingSave: false, }))
          )
      }
      )
    )
  );

  readonly SaveStepOrders = this.effect<Step[]>(
    pipe(
      tap((x) => this.patchState({ loadingSave: true })),
      withLatestFrom(this.questionnaire$),
      switchMap(([steps, questionnaire]) => {
        return this.#questionnaireApiService.saveStepOrder(questionnaire.id, steps.map((s, i) => { return { id: s.id, order: i + 1 }; }))
          .pipe(
            tapResponse(
              () => {
                this.patchState({ loadingSave: false, canUserEditQuestionnaire: true })
              },
              (error: Error) => {
                this.patchState({ error: error?.message, loadingSave: false, canUserEditQuestionnaire: false })
                this.addCannotEditTost();
              }))
      })))

  private deleteStepLocal(questionnaire: Questionnaire, currentStepIndex: number, index: number) {
    this.patchState({
      questionnaire: {
        ...questionnaire, steps: [...questionnaire.steps.filter((s, i) => i != index)]
      },
      currentStepIndex: currentStepIndex == 0 ? 0 : currentStepIndex - 1,
      loadingSave: false
    })
    this.SaveStepOrders([...questionnaire.steps.filter((s, i) => i > index)])
  }

  readonly fetchQuestionnaireById = this.effect<number>(
    pipe(
      tap((id) => this.patchState({ loading: true, questionnaireId: id })),
      switchMap((id) =>
        this.#questionnaireApiService.getQuestionnaireById(id).pipe(
          tapResponse(
            (q) => {
              if (id == 0) {
                this.#globalStore.refreshQuestionnaireAfterUpdate()
              }
              this.patchState({ questionnaire: q, questionnaireId: q.id, currentStepIndex: 0, loading: false, });
              this.fetchImagesToQuestionnaire();
              this.updateMainColor(q.definitions?.mainColor ?? "#1390CF");
              this.updateSecondaryColor(q.definitions?.secondaryColor ?? "#D1EFFF");
              this.updateBackColor(q.definitions?.backColor ?? "#F0F3FA");
              console.log("q.definitions?.mainColor", q.definitions);
            },
            (error: Error) =>
              this.patchState({ error: error?.message, loading: false })
          )
        )
      )
    )
  );

  readonly fetchQuestionnaireByGuid = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap((guid) =>
        this.#questionnaireApiService.getQuestionnaireByGuid(guid).pipe(
          tapResponse(
            (q) => {
              this.patchState({ questionnaire: q, questionnaireId: q.id, currentStepIndex: 0, loading: false });
              this.fetchImagesToQuestionnaire();
            }
            ,
            (error: Error) =>
              this.patchState({ error: error?.message, loading: false })
          )
        )
      )
    )
  );

  fetchImagesToQuestionnaire() {
    // var imgQuestion = [...this.stepsSig$()?.flatMap(s => s.questions.filter(q => q.type.id == EQuestionType.Image)) ?? []];
    // imgQuestion.forEach(x => {
    //   this.getImageByQuestionId(x.id)
    // });
  }

  readonly getImageByQuestionId = this.effect<number>(
    pipe(
      concatMap((guid) =>
        this.#questionnaireApiService.getImageByQuestioneId(guid).pipe(
          tapResponse(
            (img) => this.patchState({
              //  imagesToQuestionnaire: [...this.imagesToQuestionnaireSig$() ?? [], img] 
            }),
            (error: Error) =>
              this.patchState({ error: error?.message, })
          )
        )
      )
    )
  );

  readonly deleteImageFromServer = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loadDeleteImg: true })),
      switchMap((guid) =>
        this.#questionnaireApiService.deleteImageByQuestioneId(guid).pipe(
          tapResponse(
            (questionId) => this.patchState({

              // imagesToQuestionnaire: [...this.imagesToQuestionnaireSig$()?.filter(x => x.questionId != questionId) ?? []]

            }),
            (error: Error) =>
              this.patchState({ error: error?.message, loadDeleteImg: false })
          )
        )
      )
    )
  );
  readonly saveDefinitions = this.effect<{ definitions?: DefinitionsStyle, category?: QuestionnaireCategory }>(
    pipe(
      withLatestFrom(this.questionnaireId$, this.isUser$),
      filter(([request, questionnaireId, isUser]) => questionnaireId != 0 && isUser == false),
      tap(() => this.patchState({ loadingSave: true, })),
      switchMap(([request, questionnaireId]) =>
        this.#questionnaireApiService.saveDefinitions(request, questionnaireId).pipe(
          tapResponse(
            () => {
              this.#questionnarieGridStore.updateDefinitions({ id: questionnaireId, definitions: request.definitions, category: request.category });
              this.patchState({ loadingSave: false, canUserEditQuestionnaire: true })
            },
            (error: Error) => {
              this.patchState({ error: error?.message, loadingSave: false, canUserEditQuestionnaire: false })
              this.addCannotEditTost();
            }

          )
        )
      )
    )
  );
  readonly saveTemplateImg = this.effect<string>(
    pipe(
      withLatestFrom(this.questionnaireId$),
      filter(([templateImg, questionnaireId]) => questionnaireId != 0),
      switchMap(([templateImg, questionnaireId]) =>
        this.#questionnaireApiService.updateTemplateImg(templateImg, questionnaireId).pipe(
          tapResponse(
            () => {
              this.#questionnarieGridStore.updateTemplateImage({ id: questionnaireId, image: templateImg });
            },
            (error: Error) => {
            }

          )
        )
      )
    )
  );
  readonly saveDistributionDetails = this.effect<{ users: Recipients, mailForm: MailForm, reminder: Reminder, isPublish: boolean, isSendAll: boolean }>(
    pipe(
      tap((props) => this.patchState({ loadingSave: true, loadingDistributed: props.isPublish })),
      tap((x) => this.saveContact(x.users)),
      tap((x) => this.saveMailDetails(x.mailForm)),
      tap((x) => this.saveIsSendAll(x.isSendAll)),
      withLatestFrom(this.questionnaire$),
      switchMap(([props, questionnaire]) =>
        this.#questionnaireApiService.saveDistributionDetails(
          {
            questionnaireId: questionnaire.id,
            mailSubject: props.mailForm.subject ?? "", //cancel it can be undefined
            mailBody: props.mailForm.body,
            submissionDateNTZ: props.mailForm.submissionDateNTZ,
            reminder: props.reminder,
            recipients: props.users,
            isPublish: props.isPublish,
            isAddRecipients: false,
            sendEmail: props.mailForm.sendEmail,
            isSendAll: props.isSendAll
          } as DistributionDetailsRequest).pipe(
            tapResponse(
              (distributionDetails) => {
                this.#questionnarieGridStore.updateDistributionDetails({
                  id: questionnaire.id, recipients: distributionDetails.recipients!,
                  isPublish: props.isPublish, submissionDateNTZ: props.mailForm.submissionDateNTZ, mailForm: props.mailForm, reminder: distributionDetails.reminder,
                  isSendAll: props.isSendAll
                })
                this.patchState({
                  loadingSave: false,
                  loadingDistributed: false,
                  recentlySave: new Date(),
                  questionnaire: {
                    ...questionnaire,
                    reminder: distributionDetails.reminder,
                    recipients: distributionDetails.recipients,
                    mailBody: distributionDetails.mailBody,
                    mailSubject: distributionDetails.mailSubject,
                    submissionDateNTZ: distributionDetails.submissionDateNTZ,
                    isSendAll: distributionDetails.isSendAll
                  }
                });

                if (props.isPublish) {
                  this.#dialog.open(SendSuccessComponent,
                    {
                      position: { top: '8%', },
                      panelClass: 'dialog-panel',
                      data: { isFromPublish: true }
                    }).afterClosed()
                    .subscribe(x => {
                      if (!x)
                        this.patchState({ canUserEditQuestionnaire: false })
                      this.#router.navigate(['main'])
                    });
                }
              },
              (error: Error) => {
                this.patchState({ error: error?.message, loadingSave: false })
              }
            )
          )
      )
    )
  );

  readonly updateMainColor = this.updater((state, color: string) => ({
    ...state,
    questionnaire: { ...state.questionnaire, definitions: { name: "", secondaryColor: "", backColor: "", ...state.questionnaire.definitions, mainColor: color } }
  }));
  readonly updateSecondaryColor = this.updater((state, color: string) => ({
    ...state,
    questionnaire: { ...state.questionnaire, definitions: { name: "", mainColor: "", backColor: "", ...state.questionnaire.definitions, secondaryColor: color } }
  }));

  readonly updateBackColor = this.updater((state, color: string) => ({
    ...state,
    questionnaire: { ...state.questionnaire, definitions: { name: "", mainColor: "", secondaryColor: "", ...state.questionnaire.definitions, backColor: color } }
  }));
  addCannotEditTost() {
    this.#router.navigate(['/']);
    this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: "This questionnaire is being edited, it is not possible to edit at the same time", he: "שאלון זה בעריכה, אין אפשרות לערוך במקביל" }, position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.ERROR });
  }
  readonly saveQuestionnarieFinial = this.effect<Step>(
    pipe(
      withLatestFrom(this.canUserEditQuestionnaire$, this.changeNotSavedInCurrentStep$, this.questionnaireId$, this.definitions$, this.category$),
      filter(([step, canUserEditQuestionnaire, changeNotSavedInCurrentStep, questionnaireId, definitions, category]) => canUserEditQuestionnaire && changeNotSavedInCurrentStep),
      tap(([step, canUserEditQuestionnaire, changeNotSavedInCurrentStep, questionnaireId, definitions, category]) => {
        this.#questionnarieGridStore.updateDefinitions({
          id: questionnaireId,
          definitions: definitions,
          category: category
        })
      }),
      map(([step, canUserEditQuestionnaire, changeNotSavedInCurrentStep, questionnaireId, definitions, category]) => {
        var guid = this.#tostesStore.generateGuid();
        this.#tostesStore.addTost({ guid, message: { en: "save...", he: "שומר..." }, type: TostTypeEnum.LOAD });
        return { canUserEditQuestionnaire, changeNotSavedInCurrentStep, guid, questionnaireId, step, definitions, category }
      }),
      switchMap((props) =>
        combineLatest([
          this.#questionnaireApiService.saveDefinitions(
            { definitions: props.definitions, category: props.category },
            props.questionnaireId ?? 0
          ),
          this.#questionnaireApiService.saveStep(props.questionnaireId, props.step, false)
        ])
          .pipe(
            tapResponse(
              (x) => {
                this.#tostesStore.removeTostByGuid(props.guid)
                this.#tostesStore.addTost({ guid: "", message: { en: "Questionnaire successfully saved...", he: "השאלון נשמר בהצלחה..." }, type: TostTypeEnum.SUCCESS, seconds: 2 });
              },
              (error: Error) => {
                this.#tostesStore.removeTostByGuid(props.guid)
                this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while saving the questionnaire", he: "ארעה שגיאה בעת שמירת השאלון" }, type: TostTypeEnum.ERROR, seconds: 2 });

                this.patchState({ error: error?.message, loadingSave: false })
              }
            )
          )
      )
    )
  );
}